import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin, map, switchMap } from 'rxjs';
import { DetailCardComponent } from '../../shared/components/orders/detail-card/detail-card.component';
import { CommentDetailCardComponent } from '../../shared/components/orders/comment-detail-card/comment-detail-card.component';
import { ChipComponent } from '../../shared/components/orders/chip/chip.component';
import { DialogComponent } from '../../shared/components/orders/dialog/dialog.component';
import { DetailTableComponent } from '../../shared/components/orders/detail-table/detail-table.component';
import { ErrorComponent } from '../../shared/components/orders/error/error.component';
import { LoadingAnimationComponent } from '../../shared/components/loading-animation/loading-animation.component';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { AggregateAnalysisComponent } from '../../shared/components/orders/aggregate-analysis/aggregate-analysis.component';
import { CommentDialogComponent } from '../../shared/components/orders/comment-dialog/comment-dialog.component';
import { NotificationCardComponent } from '../../shared/components/orders/notification-card/notification-card.component';
import { TabViewModule } from 'primeng/tabview';
import { FieldsetModule } from 'primeng/fieldset';
import { AccordionModule } from 'primeng/accordion';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OrderService } from '../../core/order.service';
import { ValidationIssuesService } from '../../core/validation-issues.service';
import { FileService } from '../../shared/components/orders/file-services/file.service';
import { LoggerService } from '../../core/logger.service';
import { TokenService } from '../../core/token.service';
import { ToastService } from '../../core/toast.service';
import { Title } from '@angular/platform-browser';
import { OrderDetailResponse } from '../../shared/models/order/order-detail-response.model';
import { ValidationIssuesResponse } from '../../shared/models/validation-issues/order-response.model';
import { UserRole } from '../../core/user-role.enum';
import { OrderStatus } from '../../shared/models/status/order-status.enum';
import { HygieneInspectionStatus } from '../../shared/models/status/hygiene-inspection-status.enum';
import { CommentType } from '../../shared/models/comments/comments-type.enum';
import { ValidationSeverity } from '../../core/validation-severity.enum';
import { RemuneratedTaskHygieneInspectionType } from '../../shared/models/aggregates/remunerate-tasks-hygiene-type.enum';
import { AggregateFilterComponent } from '../../shared/components/orders/aggregate-filter/aggregate-filter.component';
import { AggregateResponse } from '../../shared/models/aggregates/aggregate-response.model';
import { AggregateReportState } from '../../shared/models/status/aggregate-report-state.enum';
import { AggregateType } from '../../shared/models/aggregates/aggregate-type.enum';
import { CustomerFrontendManagementService } from '../../core/customer-frontend-management.service';
import { OrderAccessGroupResponse } from '../../shared/models/groups/order-access-group-response.model';
import { UpdateGroupPayload } from '../../shared/models/access-control/update-group-payload.model';
import { GroupDetailResponse } from '../../shared/models/groups/group-detail-response.model';

interface AssignableOrderGroup extends OrderAccessGroupResponse {
  selected?: boolean;
  initiallySelected?: boolean;
}

@Component({
  selector: 'app-order-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TabViewModule,
    FieldsetModule,
    AccordionModule,
    DialogModule,
    ProgressSpinnerModule,
    DetailCardComponent,
    CommentDetailCardComponent,
    ChipComponent,
    DialogComponent,
    DetailTableComponent,
    ErrorComponent,
    LoadingAnimationComponent,
    ButtonComponent,
    AggregateAnalysisComponent,
    CommentDialogComponent,
    NotificationCardComponent,
    AggregateFilterComponent,
  ],
  templateUrl: './order-detail.component.html',
  styleUrl: './order-detail.component.scss',
})
export class OrderDetailComponent implements OnInit {
  @ViewChild(DialogComponent) dialogComponent!: DialogComponent;
  @ViewChild(CommentDialogComponent)
  commentDialogComponent!: CommentDialogComponent;

  orderDetail!: OrderDetailResponse;
  validationIssues!: ValidationIssuesResponse;
  idStr = '';
  isLoading = true;
  error: Error | null = null;
  isShowingAggregateAnalysis = false;
  showDeleteDialog = false;
  showAssignGroupDialog = false;

  isDownloadingValidationIssues = false;
  isDownloadingDocumentation = false;
  isDownloadingPerformance = false;
  isLoadingAssignableGroups = false;
  isSavingAssignableGroups = false;

  isValidationIssuesDisabled = true;
  validationIssuesStyle: Record<string, string> = {
    'background-color': 'lightgray',
  };

  commentType = CommentType;
  OrderStatus = OrderStatus;
  UserRole = UserRole;
  HygieneInspectionStatus = HygieneInspectionStatus;
  remuneratedTaskTypeLabels = RemuneratedTaskHygieneInspectionType;
  isDeleteButtonDisabled = false;

  types: AggregateType[] = [];
  statuses: (AggregateReportState | undefined)[] = [];

  filteredAggregates: AggregateResponse[] = [];
  assignableGroups: AssignableOrderGroup[] = [];
  private loadedAssignableGroupsCustomerId = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private orderService: OrderService,
    private validationIssuesService: ValidationIssuesService,
    private fileService: FileService,
    private loggerService: LoggerService,
    private toastService: ToastService,
    private tokenService: TokenService,
    private titleService: Title,
    private customerFrontendManagementService: CustomerFrontendManagementService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.idStr = params.get('id')!;
      this.titleService.setTitle(`Auftrag ${this.idStr} | IDR Backoffice`);
      this.getOrderById(this.idStr);
      this.getValidationIssuesFileName(this.idStr);
    });
  }

  hasUserRole(role: UserRole): boolean {
    return this.tokenService.hasRole(role);
  }

  get canOpenGroupAssignment(): boolean {
    return !!this.orderDetail?.customer?.id && !this.isLoadingAssignableGroups;
  }

  get canSaveGroupAssignments(): boolean {
    return !this.isLoadingAssignableGroups && this.assignableGroups.length > 0;
  }

  openAggregateAnalysis(): void {
    this.isShowingAggregateAnalysis = !this.isShowingAggregateAnalysis;
  }

  getArrowImage(): string {
    return this.isShowingAggregateAnalysis
      ? '/assets/double-arrow-up.svg'
      : '/assets/double-arrow-down.svg';
  }

  setOrderStatus(status: OrderStatus): void {
    this.orderService.updateOrderStatus(this.orderDetail.id, status).subscribe({
      next: (response) => {
        this.updateDetailOnChange();
        this.toastService.show('success', 'Status gesetzt', response.message);
      },
      error: () => {
        this.loggerService.error('Failed to update order status');
        this.toastService.show(
          'warn',
          'Status konnte nicht verändert werden',
          'Der Status des Auftrags konnte nicht auf Versendet geändert werden.'
        );
      },
    });
  }

  setInspectionStatus(status: HygieneInspectionStatus): void {
    this.orderService
      .updateHygieneInspectionStatus(this.orderDetail.id, status)
      .subscribe({
        next: (response) => {
          this.updateDetailOnChange();
          this.toastService.show(
            'success',
            'Hygieneinspektionsstatus gesetzt',
            response.message
          );
        },
      });
  }

  generateArtifacts(orderId: string): void {
    this.fileService.generateArtifacts(orderId).subscribe({
      next: (response) => {
        this.loggerService.log('Generate Artifacts started');
        this.toastService.show(
          'success',
          'Generate Artifacts started',
          response.message
        );
      },
    });
  }

  getValidationIssuesFileName(orderId: string): void {
    this.validationIssuesService
      .getValidationIssuesFileName(orderId)
      .subscribe({
        next: (response) => {
          this.validationIssues = response;
          if (this.validationIssues.fileName) {
            this.isValidationIssuesDisabled = false;
          }
          this.validationIssuesStyle = this.getStyleFromSeverity(
            this.validationIssues.severity,
            this.validationIssues.artifactsGenerated
          );
        },
        error: (err) => {
          console.error('Fehler beim Abrufen der Validierungsprobleme:', err);
        },
      });
  }

  downloadValidationIssuesCSV(orderNumber: string): void {
    this.isDownloadingValidationIssues = true;
    this.fileService
      .downloadValidationIssueFile(orderNumber)
      .pipe(finalize(() => (this.isDownloadingValidationIssues = false)))
      .subscribe({ next: (response) => this.handleFileDownload(response) });
  }

  downloadPerformanceReports(orderNumber: string): void {
    this.isDownloadingPerformance = true;
    this.fileService
      .downloadPerformanceReports(orderNumber)
      .pipe(finalize(() => (this.isDownloadingPerformance = false)))
      .subscribe({ next: (response) => this.handleFileDownload(response) });
  }

  downloadOrderReport(orderId: string): void {
    this.isDownloadingDocumentation = true;
    this.fileService
      .downloadOrderReport(orderId)
      .pipe(finalize(() => (this.isDownloadingDocumentation = false)))
      .subscribe({ next: (response) => this.handleFileDownload(response) });
  }

  handleFileDownload(response: any): void {
    const contentDispositionHeader =
      response.headers.get('Content-Disposition') || '';
    const fileName = this.fileService.extractFilename(contentDispositionHeader);
    const blob = response.body;
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  }

  updateDetailOnChange(): void {
    this.getOrderById(this.idStr);
  }

  openDialog(): void {
    this.dialogComponent.showDialog();
  }

  openCommentDialog(
    id: string,
    comment: string | undefined,
    type: CommentType
  ): void {
    this.commentDialogComponent.showDialog(id, comment, type);
  }

  openAssignGroupDialog(): void {
    if (!this.orderDetail?.customer?.id) {
      return;
    }

    this.showAssignGroupDialog = true;
    if (this.loadedAssignableGroupsCustomerId !== this.orderDetail.customer.id) {
      this.loadAssignableGroups();
    }
  }

  closeAssignGroupDialog(): void {
    if (this.isSavingAssignableGroups) {
      return;
    }
    this.showAssignGroupDialog = false;
  }

  toggleGroupSelection(groupId: string, checked: boolean): void {
    this.assignableGroups = this.assignableGroups.map((group) =>
      group.id === groupId ? { ...group, selected: checked } : group
    );
  }

  saveGroupAssignments(): void {
    const customerId = this.orderDetail?.customer?.id;
    const orderId = this.orderDetail?.id;
    if (!customerId || !orderId || !this.canSaveGroupAssignments) {
      return;
    }

    const changedGroups = this.assignableGroups.filter(
      (group) => !!group.selected !== !!group.initiallySelected
    );

    if (changedGroups.length === 0) {
      this.showAssignGroupDialog = false;
      return;
    }

    this.isSavingAssignableGroups = true;

    forkJoin(
      changedGroups.map((group) =>
        this.customerFrontendManagementService.getGroupDetails(customerId, group.id).pipe(
          map((detail) => this.buildGroupUpdatePayload(group, detail)),
          switchMap((payload) => this.customerFrontendManagementService.updateGroup(payload))
        )
      )
    )
      .pipe(finalize(() => (this.isSavingAssignableGroups = false)))
      .subscribe({
        next: () => {
          this.assignableGroups = this.assignableGroups.map((group) => ({
            ...group,
            initiallySelected: group.selected,
          }));
          this.toastService.show(
            'success',
            'Gruppe aktualisiert',
            'Der Auftrag wurde den ausgewählten Gruppen zugewiesen.'
          );
          this.showAssignGroupDialog = false;
        },
        error: () => {
          // Fehler-Toast wird zentral im Service ausgelöst
        },
      });
  }

  toggleDeleteDialog(): void {
    this.showDeleteDialog = !this.showDeleteDialog;
  }

  deleteOrder(id: string): void {
    this.isDeleteButtonDisabled = true;
    this.orderService.deleteOrder(id).subscribe({
      next: (response) => {
        this.router.navigate(['/order']);
        this.toastService.show('success', 'Auftrag gelöscht', response.message);
      },
    });
  }

  formatDate(inputDate: string): string {
    const date = new Date(inputDate);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  navigateToZoho(zohoId: string): void {
    window.open(
      `https://crm.zoho.eu/crm/org20093979379/tab/Accounts/${zohoId}`,
      '_blank'
    );
  }

  copyOrderNumber(): void {
    const text = this.orderDetail?.orderNumber;
    if (!text || !navigator.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(text)
      .then(() =>
        this.toastService.show('success', 'Kopiert', 'Auftragsnummer kopiert')
      )
      .catch(() =>
        this.toastService.show(
          'warn',
          'Kopieren fehlgeschlagen',
          'Auftragsnummer konnte nicht kopiert werden'
        )
      );
  }

  getTaskLabel(taskType: string): string {
    return (
      Object.entries(this.remuneratedTaskTypeLabels).find(
        ([key]) => key === taskType
      )?.[1] || taskType
    );
  }

  hasValidTasks(orderDetail: OrderDetailResponse): boolean {
    return (
      orderDetail.hygiene?.remuneratedTasks?.some((task) =>
        task.type?.trim()
      ) ?? false
    );
  }

  applyClientSideFilter(filter: any): void {
    const {
      idrId,
      name,
      type,
      status,
      location,
      customerId,
      inspectionDateRange
    } = filter;

    this.filteredAggregates = this.orderDetail.aggregates.filter(agg => {
      if (idrId && !agg.idrId?.toLowerCase().includes(idrId.toLowerCase())) {
        return false;
      }
      if (name && !agg.name?.toLowerCase().includes(name.toLowerCase())) {
        return false;
      }
      if (type?.length && !type.includes(agg.type)) {
        return false;
      }
      const locString = [agg.buildingName, agg.floor, agg.room]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (location && !locString.includes(location.toLowerCase())) {
        return false;
      }
      if (customerId && !agg.customerProvidedId?.toLowerCase().includes(customerId.toLowerCase())) {
        return false;
      }
      const inspectionStatus = agg.hygiene?.status || agg.energetic?.status;
      if (status?.length && !status.includes(inspectionStatus)) {
        return false;
      }
      if (inspectionDateRange && inspectionDateRange.length === 2) {
        const [start, end] = inspectionDateRange;
        const inspectionDate = agg.hygiene?.inspectionDate || agg.energetic?.inspectionDate;
        const inspDate = inspectionDate ? new Date(inspectionDate) : null;
        if (!inspDate) {
          return false;
        }
        if (start && inspDate < new Date(start)) {
          return false;
        }
        if (end && inspDate > new Date(end)) {
          return false;
        }
      }
      return true;
    });
  }

  private getOrderById(id: string): void {
    this.orderService
      .getOrderDetail(id)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => {
          this.loggerService.log('Fetch order details completed');
          this.orderDetail = data;
          this.isLoading = false;
          this.filteredAggregates = this.orderDetail.aggregates;

          this.types = [...new Set(data.aggregates.map(agg => agg.type).filter(Boolean))];
          this.statuses = [...new Set(data.aggregates.map(agg => agg.aggregateReport?.reportState).filter(Boolean))];
        },
        error: (error) => {
          this.loggerService.error('Error fetching order details');
          this.error = error;
        },
      });
  }

  private loadAssignableGroups(): void {
    const customerId = this.orderDetail?.customer?.id;
    if (!customerId) {
      return;
    }

    this.isLoadingAssignableGroups = true;
    this.customerFrontendManagementService
      .getGroupsByCustomer(customerId)
      .pipe(finalize(() => (this.isLoadingAssignableGroups = false)))
      .subscribe({
        next: (groups) => {
          this.loadedAssignableGroupsCustomerId = customerId;
          this.assignableGroups = groups.map((group) => ({
            ...group,
            selected: (group.orderTitles ?? []).includes(this.orderDetail.orderNumber),
            initiallySelected: (group.orderTitles ?? []).includes(this.orderDetail.orderNumber),
          }));
        },
        error: () => {
          this.assignableGroups = [];
        },
      });
  }

  private buildGroupUpdatePayload(
    group: AssignableOrderGroup,
    detail: GroupDetailResponse
  ): UpdateGroupPayload {
    const existingOrderIds = detail.orders?.map((order) => order.orderId) ?? [];
    const orderIds = group.selected
      ? Array.from(new Set([...existingOrderIds, this.orderDetail.id]))
      : existingOrderIds.filter((orderId) => orderId !== this.orderDetail.id);

    return {
      groupId: group.id,
      customerId: this.orderDetail.customer.id,
      name: detail.name || group.name,
      orderIds,
      assignments: (detail.users ?? []).map((user) => ({
        userId: user.userId,
        userRole: user.userRole,
      })),
    };
  }

  private getStyleFromSeverity(
    severity: string,
    artifactsGenerated: boolean
  ): Record<string, string> {
    if (severity === ValidationSeverity.MANDATORY) {
      return { 'background-color': '#EC7063' };
    }
    if (severity === ValidationSeverity.RECOMMENDED) {
      return { 'background-color': '#F4D03F' };
    }
    if (severity === ValidationSeverity.VOLUNTARY) {
      return { 'background-color': '#52BE80' };
    }
    if (severity === ValidationSeverity.INFORMATIONAL) {
      return { 'background-color': '#696969' };
    }
    if (artifactsGenerated) {
      return { 'background-color': '#c5edd6' };
    }
    return { 'background-color': 'lightgray' };
  }
}
